/* eslint-disable react/prop-types */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { InfoIcon } from '../../utils/commonIcons';

function lastFiveLabel(selectedValueDisplay) {
  const v = String(selectedValueDisplay || '').toLowerCase();
  if (v === 'release') {
    return 'releases';
  }
  return 'sprints';
}

const ROWS = [
  {
    key: 'summary',
    label: 'Summary',
    leftBg: 'rgba(59,130,246,0.16)',
    borderLeft: '#3B82F6',
    text: (ctx) =>
      `Blue = commitment, green = completed, for the last five ${ctx.lastFive}. Green check = completed reached the commitment value for that bar. Red cross = it did not (completed below that commitment).`,
  },
  {
    key: 'withAdded',
    label: 'With added',
    leftBg: 'rgba(34,197,94,0.16)',
    borderLeft: '#22C55E',
    text: () =>
      'Committed and completed follow current scope; sub-tasks and Epics are excluded from both. They change when work is removed from the schedule, moved elsewhere (for example to another sprint), or when story points or hours increase or decrease.',
  },
  {
    key: 'withoutAdded',
    label: 'Without added',
    leftBg: 'rgba(245,158,11,0.14)',
    borderLeft: '#F59E0B',
    text: () =>
      'Committed (blue) is fixed from planned-at-start scope (sub-tasks excluded from that planned rollup). Sub-tasks and Epics are excluded from committed and completed. Completed (green) still updates from current scope—what is done.',
  },
];

function SprintGoalSuccessTooltipContent({ lastFive }) {
  const ctx = { lastFive };

  const cellRight = {
    verticalAlign: 'top',
    padding: '8px 10px',
    borderRadius: '0 6px 6px 0',
    background: 'rgba(148,163,184,0.1)',
    color: '#CBD5E1',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  };

  return (
    <div style={{ textAlign: 'left', maxWidth: 'min(420px, calc(100vw - 24px))', boxSizing: 'border-box' }}>
      <table
        role="presentation"
        cellPadding={0}
        cellSpacing={0}
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0 6px',
          margin: 0,
          fontSize: 12,
        }}
      >
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.key}>
              <td
                style={{
                  verticalAlign: 'top',
                  padding: '8px 10px',
                  borderRadius: '6px 0 0 6px',
                  background: row.leftBg,
                  borderLeft: `3px solid ${row.borderLeft}`,
                  color: '#F8FAFC',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  width: '1%',
                }}
              >
                {row.label}
              </td>
              <td style={cellRight}>{row.text(ctx)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Uses react-tooltip v5 `render` instead of data-tooltip-html so large HTML is not stuffed into a DOM
 * attribute (which can fail silently). Also keeps a stable unique id per widget instance.
 */
export default function SprintGoalSuccessTooltipIcon({ theme, selectedValueDisplay, placement = 'top-end' }) {
  const tooltipIdRef = useRef(`tooltip-standup-sprint-goal-${Math.random().toString(36).slice(2, 11)}`);
  const tooltipId = tooltipIdRef.current;
  const lastFive = lastFiveLabel(selectedValueDisplay);

  const tipStyle = {
    backgroundColor: theme === 'dark' ? '#173A5A' : '#0D1621',
    borderStyle: theme === 'dark' ? 'solid' : 'none',
    borderWidth: theme === 'dark' ? '1px' : '0px',
    borderColor: theme === 'dark' ? '#224F78' : 'transparent',
    color: theme === 'dark' ? 'white' : '#FFFFFF',
    zIndex: 99999,
    padding: '8px',
    borderRadius: '5px',
    maxWidth: 'min(480px, calc(100vw - 24px))',
    whiteSpace: 'normal',
    fontSize: '12px',
    lineHeight: 1.45,
  };

  const tooltipNode = (
    <Tooltip
      id={tooltipId}
      place={placement}
      float={false}
      positionStrategy="fixed"
      arrowColor={theme === 'dark' ? '#173A5A' : '#0D1621'}
      opacity={1}
      style={tipStyle}
      render={() => <SprintGoalSuccessTooltipContent lastFive={lastFive} />}
    />
  );

  return (
    <>
      <div className="relative z-[100] shrink-0 inline-flex">
        <span
          data-tooltip-id={tooltipId}
          data-tooltip-place={placement}
          className="cursor-pointer inline-flex"
        >
          <InfoIcon className={`w-4 h-4 ${theme === 'light' ? 'text-[#5580A6]' : 'text-[#A3B1C9]'}`} />
        </span>
      </div>
      {typeof document !== 'undefined' ? createPortal(tooltipNode, document.body) : null}
    </>
  );
}

SprintGoalSuccessTooltipIcon.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  selectedValueDisplay: PropTypes.string.isRequired,
  placement: PropTypes.string,
};
